import React from "react";

export const WIDTH_F = { full: 1, "1/2": 0.5, "1/3": 1 / 3, "2/3": 2 / 3 };
export const elWf = (el) => WIDTH_F[(el.config || {}).width || "full"] || 1;

export function groupRows(els) {
  const rows = [];
  let cur = [];
  let acc = 0;
  const flush = () => { if (cur.length) { rows.push(cur); cur = []; acc = 0; } };
  for (const el of els) {
    const f = elWf(el);
    if (f >= 0.999) { flush(); rows.push([el]); }
    else { if (acc + f > 1.001) flush(); cur.push(el); acc += f; }
  }
  flush();
  return rows;
}

export function tokFor(report, params = {}) {
  const now = new Date();
  const map = {
    report: report?.name || "Report",
    date: now.toISOString().slice(0, 10),
    time: `${String(now.getUTCHours()).padStart(2, "0")}:${String(now.getUTCMinutes()).padStart(2, "0")} UTC`,
    preparedBy: "", page: "1", pages: "1",
  };
  (report?.parameters || []).forEach((p) => {
    if (!p.name) return;
    const v = params[p.name] ?? p.default ?? "";
    map[`param:${p.name}`] = String(v);
  });
  return (s) => String(s || "").replace(/\{([\w:]+)\}/g, (m, k) => (k in map ? map[k] : m));
}

export function PreviewTable({ data }) {
  return (
    <table className="w-full border-collapse my-2 text-[11px]">
      <thead><tr>{data.columns.map((c) => <th key={c} className="text-left px-2 py-1 text-white" style={{ background: "var(--rd-primary)" }}>{String(c).replace(/_/g, " ")}</th>)}</tr></thead>
      <tbody>
        {data.blocks.slice(0, 300).map((b, i) => b.kind === "group" ? (
          <tr key={i}><td colSpan={data.columns.length} className="px-2 py-1 font-bold border border-[#dfe3ee]"
            style={{ background: b.level === 2 ? "#f4f6fb" : "#eef1f8", paddingLeft: b.level === 2 ? 24 : 8 }}>{b.label}</td></tr>
        ) : (
          <tr key={i} className={b.kind !== "row" ? "font-bold" : ""}>
            {b.cells.map((c, j) => (
              <td key={j} className="px-2 py-0.5 border border-[#dfe3ee]"
                style={{ color: c.c || undefined, background: c.bg || undefined, borderTop: b.kind === "total" ? "2px solid var(--rd-primary)" : undefined }}>{c.v}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function PreviewElement({ el, tok, payload, selected, onSelect }) {
  const cfg = el.config || {};
  let body = null;
  if (payload?.error) {
    body = <div className="text-[11px] italic text-amber-600 py-1">{payload.error}</div>;
  } else if (el.type === "heading") {
    const sizes = { 1: "text-2xl", 2: "text-lg", 3: "text-sm" };
    body = <div className={`font-bold ${sizes[cfg.level] || sizes[1]}`} style={{ color: "var(--rd-primary)" }}>{tok(cfg.text)}</div>;
  } else if (el.type === "text") {
    body = (
      <p style={{ fontSize: `${cfg.size || 12}px`, textAlign: cfg.align || "left", color: cfg.color || undefined, fontWeight: cfg.bold ? 700 : 400, fontStyle: cfg.italic ? "italic" : "normal", whiteSpace: "pre-wrap" }}>
        {tok(cfg.text)}
      </p>
    );
  } else if (el.type === "image") {
    body = cfg.url ? (
      <div style={{ textAlign: cfg.align || "center" }}><img src={cfg.url} alt="" style={{ maxHeight: `${cfg.height || 120}px`, maxWidth: "100%", display: "inline-block" }} /></div>
    ) : <div className="text-[11px] italic text-[color:var(--ink-3)] py-1">Set an image URL →</div>;
  } else if (el.type === "divider") {
    body = <hr className="border-t border-[#d7dbe6]" />;
  } else if (el.type === "spacer") {
    body = <div style={{ height: `${cfg.height || 16}px` }} className="border border-dashed border-transparent" />;
  } else if (el.type === "pagebreak") {
    body = <div className="flex items-center gap-2 text-[10px] text-[color:var(--ink-3)] uppercase tracking-wider"><span className="flex-1 border-t border-dashed border-[#c3c9d9]" />page break<span className="flex-1 border-t border-dashed border-[#c3c9d9]" /></div>;
  } else if (el.type === "kpi") {
    body = payload?.values?.length ? (
      <div className="flex gap-3 my-1 flex-wrap">
        {payload.values.map((v, i) => (
          <div key={i} className="border border-[#e3e6ef] rounded-lg px-4 py-2 text-center min-w-[110px]">
            <div className="text-xl font-bold" style={{ color: "var(--rd-primary)" }}>{v.value}</div>
            <div className="text-[10px] text-[#6B7280]">{v.label}</div>
          </div>
        ))}
      </div>
    ) : <div className="text-[11px] italic text-[color:var(--ink-3)] py-1">Bind a dataset + measures →</div>;
  } else if (el.type === "chart") {
    body = payload?.png ? (
      <img src={`data:image/png;base64,${payload.png}`} alt="chart" className="max-w-full my-1" />
    ) : <div className="text-[11px] italic text-[color:var(--ink-3)] py-2 border border-dashed border-[#d7dbe6] rounded text-center">Bind a dataset, category and measures →</div>;
  } else if (el.type === "table") {
    body = payload?.columns ? <PreviewTable data={payload} /> : <div className="text-[11px] italic text-[color:var(--ink-3)] py-2 border border-dashed border-[#d7dbe6] rounded text-center">Bind a dataset, columns and measures →</div>;
  } else if (el.type === "matrix") {
    body = payload?.columns ? <PreviewTable data={payload} /> : <div className="text-[11px] italic text-[color:var(--ink-3)] py-2 border border-dashed border-[#d7dbe6] rounded text-center">Bind a dataset, row/column fields and a measure →</div>;
  }
  if (!onSelect) return <div data-testid={`rd-preview-el-${el.id}`}>{body}</div>;
  return (
    <div data-testid={`rd-preview-el-${el.id}`} onClick={(e) => { e.stopPropagation(); onSelect(); }}
      className={`rounded px-1 -mx-1 cursor-pointer transition-colors ${selected ? "ring-2 ring-[color:var(--brand-ocean)] bg-[color:var(--brand-ocean)]/5" : "hover:bg-black/[0.025]"}`}>
      {body}
    </div>
  );
}

const PAGE_MM = { A4: [210, 297], A3: [297, 420], A5: [148, 210], Letter: [216, 279], Legal: [216, 356] };

export function pageDims(report) {
  const pg = report?.page || {};
  const dims = PAGE_MM[pg.size || "A4"] || PAGE_MM.A4;
  const wmm = pg.orientation === "landscape" ? dims[1] : dims[0];
  return { pageW: Math.min(920, Math.round(wmm * 3.9)), padPx: Math.round((pg.margin ?? 15) * 3.2) };
}

export function ReportPaper({ report, tok, preview = {}, selectedId, onSelectEl, emptyHint, branding }) {
  const els = report.elements || [];
  const hdr = report.header || {};
  const ftr = report.footer || {};
  const { pageW, padPx } = pageDims(report);
  const rows = groupRows(els);
  const brand = branding || report.branding || null;
  const showBrand = brand && (brand.logo_url || brand.app_name);
  const renderEl = (el) => (
    <PreviewElement key={el.id} el={el} tok={tok} payload={preview[el.id]} selected={selectedId === el.id}
      onSelect={onSelectEl ? () => onSelectEl(el.id) : undefined} />
  );
  return (
    <div data-testid="rd-preview" className="mx-auto bg-white rounded-sm shadow-[0_2px_18px_rgba(15,23,42,0.14)] relative text-[#1a2233]"
      style={{ width: `${pageW}px`, minHeight: "540px", padding: `${padPx}px`, "--rd-primary": (brand?.primary_color) || report.theme?.primary || "#2B4EFF" }}>
      {(report.watermark?.text || "") && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden">
          <span className="font-bold text-[52px] rotate-[-35deg]" style={{ color: `rgba(80,90,110,${report.watermark.opacity ?? 0.08})` }}>{report.watermark.text}</span>
        </div>
      )}
      {hdr.show !== false && (hdr.left || hdr.right) && (
        <div className="absolute top-2 left-0 right-0 flex justify-between text-[9px] text-[#6B7280]" style={{ paddingLeft: padPx, paddingRight: padPx }}>
          <span>{tok(hdr.left)}</span><span>{tok(hdr.right)}</span>
        </div>
      )}
      {showBrand && (
        <div data-testid="rd-preview-brand-bar" className="flex items-center justify-between mb-3 pb-2 relative" style={{ borderBottom: `2px solid ${brand.primary_color || "#2B4EFF"}` }}>
          {brand.logo_url ? <img src={brand.logo_url} alt="logo" style={{ maxHeight: 30, maxWidth: 140 }} /> : <span />}
          {brand.app_name && <span className="text-[12px] font-bold" style={{ color: brand.primary_color || "#2B4EFF" }}>{brand.app_name}</span>}
        </div>
      )}
      <div className="space-y-2 relative">
        {els.length === 0 ? (
          <div className="text-center text-sm text-[#9aa1b2] py-24">{emptyHint || "This report has no elements yet."}</div>
        ) : rows.map((row, ri) => (
          row.length === 1 && elWf(row[0]) >= 0.999 ? renderEl(row[0]) : (
            <div key={`row-${ri}`} className="flex gap-3 items-start">
              {row.map((el) => (
                <div key={el.id} style={{ flex: `0 0 calc(${(elWf(el) * 100).toFixed(1)}% - 8px)`, minWidth: 0 }}>{renderEl(el)}</div>
              ))}
            </div>
          )
        ))}
      </div>
      {ftr.show !== false && (ftr.left || ftr.center || ftr.right) && (
        <div className="absolute bottom-2 left-0 right-0 flex justify-between text-[9px] text-[#6B7280]" style={{ paddingLeft: padPx, paddingRight: padPx }}>
          <span>{tok(ftr.left)}</span><span>{tok(ftr.center)}</span><span>{tok(ftr.right)}</span>
        </div>
      )}
    </div>
  );
}
